import { Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useRef } from "react";
import { Edge, Node, XYPosition } from "@xyflow/react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { ISequence } from "../../models/interactions/Sequence";
import { SequenceNodeData } from "./SequenceNode";
import {
  AltDragState,
  EdgePathGeometry,
  extractNodeFromSpliceableChain,
  findSpliceableEdgeNearPoint,
  getNodeCenterPoint,
  getSpliceableEdgesForNode,
  insertNodeIntoEdge,
  updateNodePositionInList,
} from "./SequenceCanvasSplice";

type UseSequenceCanvasAltSpliceParams = {
  interaction: ISequence;
  updateSequence: (sequence: ISequence) => void;
  nodesRef: MutableRefObject<Node<SequenceNodeData>[]>;
  edgesRef: MutableRefObject<Edge[]>;
  setNodes: Dispatch<SetStateAction<Node<SequenceNodeData>[]>>;
  setEdges: Dispatch<SetStateAction<Edge[]>>;
};

export function useSequenceCanvasAltSplice({
  interaction,
  updateSequence,
  nodesRef,
  edgesRef,
  setNodes,
  setEdges,
}: UseSequenceCanvasAltSpliceParams) {
  const activeAltDragRef = useRef<AltDragState | null>(null);
  const altIsDownRef = useRef(false);
  const edgePathGeometryRef = useRef<Map<string, EdgePathGeometry>>(new Map());
  const latestDraggedNodeRef = useRef<Node<SequenceNodeData> | null>(null);

  const syncDraggedNodeFromNodes = useCallback(
    (appliedNodes: Node<SequenceNodeData>[]) => {
      const activeAltDrag = activeAltDragRef.current;
      if (!activeAltDrag) {
        return;
      }

      const draggedNode = appliedNodes.find(
        (node) => node.id === activeAltDrag.nodeId,
      );
      if (!draggedNode) {
        return;
      }

      latestDraggedNodeRef.current = {
        ...draggedNode,
        position: { ...draggedNode.position },
      };
    },
    [],
  );

  const tryInsertDraggedNode = useCallback(
    (node: Node<SequenceNodeData>) => {
      const activeAltDrag = activeAltDragRef.current;
      if (!activeAltDrag || activeAltDrag.nodeId !== node.id) {
        return false;
      }

      const currentNodes = updateNodePositionInList(nodesRef.current, node);
      latestDraggedNodeRef.current = {
        ...node,
        position: { ...node.position },
      };

      const draggedNode = currentNodes.find((currentNode) => currentNode.id === node.id);
      const dropPoint = draggedNode ? getNodeCenterPoint(draggedNode) : null;
      if (!dropPoint) {
        setNodes(currentNodes);
        return false;
      }

      const insertionTarget = findSpliceableEdgeNearPoint(
        edgesRef.current.filter(
          (edge) => edge.source !== node.id && edge.target !== node.id,
        ),
        edgePathGeometryRef.current,
        dropPoint,
      );

      if (!insertionTarget) {
        setNodes(currentNodes);
        return false;
      }

      const insertedEdges = insertNodeIntoEdge(
        edgesRef.current,
        insertionTarget,
        node.id,
      );
      activeAltDragRef.current = {
        nodeId: node.id,
        phase: "connected",
      };
      setNodes(currentNodes);
      setEdges(insertedEdges);
      return true;
    },
    [edgesRef, nodesRef, setEdges, setNodes],
  );

  const tryExtractDraggedNode = useCallback(
    (node: Node<SequenceNodeData>) => {
      const activeAltDrag = activeAltDragRef.current;
      if (!activeAltDrag || activeAltDrag.nodeId !== node.id) {
        return false;
      }

      const extractedEdges = extractNodeFromSpliceableChain(
        edgesRef.current,
        node.id,
      );
      if (!extractedEdges) {
        return false;
      }

      const currentNodes = updateNodePositionInList(nodesRef.current, node);
      latestDraggedNodeRef.current = {
        ...node,
        position: { ...node.position },
      };
      activeAltDragRef.current = {
        nodeId: node.id,
        phase: "detached",
      };
      setNodes(currentNodes);
      setEdges(extractedEdges);
      return true;
    },
    [edgesRef, nodesRef, setEdges, setNodes],
  );

  const handleAltActionForNode = useCallback(
    (node: Node<SequenceNodeData>) => {
      const activeAltDrag = activeAltDragRef.current;
      if (!activeAltDrag || activeAltDrag.nodeId !== node.id) {
        return;
      }

      const { incoming, outgoing } = getSpliceableEdgesForNode(
        edgesRef.current,
        node.id,
      );
      const isDetached = incoming.length === 0 && outgoing.length === 0;
      const isSimpleChain = incoming.length === 1 && outgoing.length === 1;

      if (isSimpleChain) {
        tryExtractDraggedNode(node);
        return;
      }

      if (!isDetached) {
        return;
      }

      const inserted = tryInsertDraggedNode(node);
      if (inserted) {
        return;
      }

      const currentNodes = updateNodePositionInList(nodesRef.current, node);
      latestDraggedNodeRef.current = {
        ...node,
        position: { ...node.position },
      };
      activeAltDragRef.current = {
        nodeId: node.id,
        phase: "detached",
      };
      setNodes(currentNodes);
    },
    [edgesRef, nodesRef, setNodes, tryExtractDraggedNode, tryInsertDraggedNode],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Alt") {
        return;
      }

      if (event.repeat || altIsDownRef.current) {
        return;
      }

      altIsDownRef.current = true;

      const activeAltDrag = activeAltDragRef.current;
      const draggedNode = latestDraggedNodeRef.current;
      if (!activeAltDrag || !draggedNode) {
        return;
      }

      handleAltActionForNode(draggedNode);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key !== "Alt") {
        return;
      }

      altIsDownRef.current = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleAltActionForNode]);

  const onNodeDragStart = useCallback(
    (event: ReactMouseEvent | MouseEvent, node: Node<SequenceNodeData>) => {
      activeAltDragRef.current = {
        nodeId: node.id,
        phase: "normal",
      };
      latestDraggedNodeRef.current = {
        ...node,
        position: { ...node.position },
      };
      altIsDownRef.current = "altKey" in event && Boolean(event.altKey);

      if (!altIsDownRef.current) {
        return;
      }

      handleAltActionForNode(node);
    },
    [handleAltActionForNode],
  );

  const onNodeDrag = useCallback(
    (event: ReactMouseEvent | MouseEvent, node: Node<SequenceNodeData>) => {
      const activeAltDrag = activeAltDragRef.current;
      if (!activeAltDrag || activeAltDrag.nodeId !== node.id) {
        return;
      }

      latestDraggedNodeRef.current = {
        ...node,
        position: { ...node.position },
      };

      const isAltPressed = "altKey" in event && Boolean(event.altKey);
      if (!isAltPressed) {
        altIsDownRef.current = false;
        return;
      }

      if (!altIsDownRef.current) {
        altIsDownRef.current = true;
        handleAltActionForNode(node);
      }
    },
    [handleAltActionForNode],
  );

  const onNodeDragStop = useCallback(
    (event: ReactMouseEvent | MouseEvent, node: Node<SequenceNodeData>) => {
      const activeAltDrag = activeAltDragRef.current;
      if (!activeAltDrag || activeAltDrag.nodeId !== node.id) {
        return;
      }

      activeAltDragRef.current = null;
      latestDraggedNodeRef.current = null;
      altIsDownRef.current = false;

      if (activeAltDrag.phase === "normal") {
        return;
      }

      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;
      const finalNodes = updateNodePositionInList(currentNodes, node);

      if (activeAltDrag.phase === "connected") {
        setNodes(finalNodes);
        setEdges(currentEdges);
        updateSequence({
          ...interaction,
          nodes: finalNodes,
          edges: currentEdges,
        } as ISequence);
        return;
      }

      const draggedNode = finalNodes.find((currentNode) => currentNode.id === node.id);
      const dropPoint = draggedNode ? getNodeCenterPoint(draggedNode) : null;
      const targetEdge =
        dropPoint === null
          ? null
          : findSpliceableEdgeNearPoint(
              currentEdges.filter(
                (edge) => edge.source !== node.id && edge.target !== node.id,
              ),
              edgePathGeometryRef.current,
              dropPoint,
            );
      const finalEdges = targetEdge
        ? insertNodeIntoEdge(currentEdges, targetEdge, node.id)
        : currentEdges;

      setNodes(finalNodes);
      setEdges(finalEdges);
      updateSequence({
        ...interaction,
        nodes: finalNodes,
        edges: finalEdges,
      } as ISequence);
    },
    [edgesRef, interaction, nodesRef, setEdges, setNodes, updateSequence],
  );

  const onEdgePathGeometryChange = useCallback(
    (edgeId: string, points: XYPosition[]) => {
      edgePathGeometryRef.current.set(edgeId, { points });
    },
    [],
  );

  const onEdgePathGeometryRemove = useCallback((edgeId: string) => {
    edgePathGeometryRef.current.delete(edgeId);
  }, []);

  const pruneEdgeGeometry = useCallback((edges: Edge[]) => {
    const validEdgeIds = new Set(edges.map((edge) => edge.id));
    edgePathGeometryRef.current.forEach((_geometry, edgeId) => {
      if (!validEdgeIds.has(edgeId)) {
        edgePathGeometryRef.current.delete(edgeId);
      }
    });
  }, []);

  return {
    activeAltDragRef,
    syncDraggedNodeFromNodes,
    pruneEdgeGeometry,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onEdgePathGeometryChange,
    onEdgePathGeometryRemove,
  };
}
